// useOutboundIntegrations.js

import { useCallback, useEffect, useMemo, useState } from 'react';

// Utilities
import { toast } from 'react-toastify';
import { cleanString } from '../../apiUtils';
import { normalizeRuleEvent } from '../utils/integrationUtils';

// Services
import {
    createBaseWebhookRule,
    deleteBaseWebhookRule,
    listBaseWebhookRules,
    testBaseWebhookRule,
    updateBaseWebhookEndpoint,
    updateBaseWebhookRule,
} from 'services/api/ApiAccessService';

/**
 * useOutboundIntegrations
 *
 * Manages outbound rule data, modal state, and API-side actions for integrations.
 *
 * @param {string} baseId - The active base identifier.
 * @param {boolean} canManageCredentials - Whether the current user can manage credentials.
 * @param {array} typeRows - Normalized schema type rows.
 * @param {Function} setErrorMessage - Setter for page-level error messaging.
 * @param {Function} setStatusMessage - Setter for page-level status messaging.
 * @param {Function} setBusyActionKey - Setter for page-level busy action indicator.
 *
 * @returns {object} Outbound integrations state and handlers.
 */
const useOutboundIntegrations = ({
    baseId,
    canManageCredentials,
    typeRows,
    setErrorMessage,
    setStatusMessage,
    setBusyActionKey,
}) => {

    // ----- STATE ----- //

    // Outbound Loading State
    const [loading, setLoading] = useState(false);

    // Outbound Rules Database State
    const [rules, setRules] = useState([]);

    // Create Modal Open State
    const [createModalOpen, setCreateModalOpen] = useState(false);

    // Create Draft Name State
    const [draftName, setDraftName] = useState('');

    // Create Draft Event State
    const [draftEvent, setDraftEvent] = useState('node.created');

    // Create Draft Type ID State
    const [draftTypeId, setDraftTypeId] = useState('');

    // Create Draft Destination URL State
    const [draftDestinationUrl, setDraftDestinationUrl] = useState('');

    // Create Draft Destination Name State
    const [draftDestinationName, setDraftDestinationName] = useState('');

    // Edit Modal Open State
    const [editModalOpen, setEditModalOpen] = useState(false);

    // Editing Rule State
    const [editingRule, setEditingRule] = useState(null);

    // Edit Draft Name State
    const [editDraftName, setEditDraftName] = useState('');

    // Edit Draft Event State
    const [editDraftEvent, setEditDraftEvent] = useState('node.created');

    // Edit Draft Type ID State
    const [editDraftTypeId, setEditDraftTypeId] = useState('');

    // Edit Draft Status State
    const [editDraftStatus, setEditDraftStatus] = useState('active');

    // Edit Draft Destination URL State
    const [editDraftDestinationUrl, setEditDraftDestinationUrl] = useState('');

    // Edit Draft Destination Name State
    const [editDraftDestinationName, setEditDraftDestinationName] = useState('');

    // Delete Confirm Open State
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    // Pending Delete Rule State
    const [pendingDeleteRule, setPendingDeleteRule] = useState(null);


    // ----- MEMOS ----- //

    // Memoized descending outbound rules by update/create timestamp.
    const sortedRules = useMemo(() => {
        return [...rules].sort((a, b) => {
            const aTime = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
            const bTime = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
            return bTime - aTime;
        });
    }, [rules]);

    // Memoized delete confirmation message for outbound rules.
    const deleteConfirmMessage = useMemo(() => {
        const label = cleanString(pendingDeleteRule?.name || 'this rule');
        return `Delete outbound rule "${label}"? This cannot be undone.`;
    }, [pendingDeleteRule]);


    // ----- UTILITIES ----- //

    const loadRules = useCallback(async () => {
        if (!baseId) return;

        setLoading(true);
        setErrorMessage('');

        try {
            if (!canManageCredentials) {
                setRules([]);
                return;
            }

            const response = await listBaseWebhookRules({ baseId });
            setRules(Array.isArray(response?.data) ? response.data : []);
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to load outbound rules.');
        } finally {
            setLoading(false);
        }
    }, [canManageCredentials, setErrorMessage, baseId]);


    // ----- EFFECTS ----- //

    // Load outbound rules when base or permission context changes.
    useEffect(() => {
        loadRules();
    }, [loadRules]);


    // ----- HANDLERS ----- //

    /**
     * Resets outbound rule create draft values to defaults.
     */
    const resetDraft = useCallback(() => {
        setDraftName('');
        setDraftEvent('node.created');
        setDraftTypeId(typeRows[0]?.typeId || '');
        setDraftDestinationUrl('');
        setDraftDestinationName('');
    }, [typeRows]);

    /**
     * Opens the outbound create modal and resets draft state.
     */
    const handleOpenCreateModal = useCallback(() => {
        if (!canManageCredentials) return;
        setErrorMessage('');
        setStatusMessage('');
        resetDraft();
        setCreateModalOpen(true);
    }, [canManageCredentials, resetDraft, setErrorMessage, setStatusMessage]);

    /**
     * Creates an outbound rule and refreshes rule rows.
     */
    const handleCreateRule = useCallback(async () => {
        if (!baseId || !canManageCredentials) return;

        const name = cleanString(draftName);
        const event = normalizeRuleEvent(draftEvent);
        const typeId = cleanString(draftTypeId);
        const destinationUrl = cleanString(draftDestinationUrl);
        const destinationName = cleanString(draftDestinationName);

        if (!name || !typeId || !destinationUrl) {
            setErrorMessage('Rule name, trigger type, and destination URL are required.');
            return;
        }

        setBusyActionKey('create-rule');
        setErrorMessage('');
        setStatusMessage('');

        try {
            await createBaseWebhookRule({
                baseId,
                name,
                typeId,
                event,
                destinationUrl,
                destinationName,
            });

            toast.success('Outbound rule created.');
            setStatusMessage('Outbound rule created.');
            setCreateModalOpen(false);
            await loadRules();
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to create outbound rule.');
        } finally {
            setBusyActionKey('');
        }
    }, [
        canManageCredentials,
        draftDestinationName,
        draftDestinationUrl,
        draftEvent,
        draftName,
        draftTypeId,
        loadRules,
        setBusyActionKey,
        setErrorMessage,
        setStatusMessage,
        baseId,
    ]);

    /**
     * Opens outbound edit modal and seeds edit draft values.
     *
     * @param {object} rule - Outbound rule row to edit.
     */
    const handleOpenEditModal = useCallback((rule) => {
        if (!canManageCredentials) return;
        const nextRule = rule || null;
        setEditingRule(nextRule);
        setEditDraftName(cleanString(nextRule?.name));
        setEditDraftEvent(normalizeRuleEvent(nextRule?.event));
        setEditDraftTypeId(cleanString(nextRule?.typeId || nextRule?.typeIdLower));
        setEditDraftStatus(cleanString(nextRule?.status || 'active').toLowerCase() === 'paused' ? 'paused' : 'active');
        setEditDraftDestinationUrl(cleanString(nextRule?.endpoint?.url));
        setEditDraftDestinationName(cleanString(nextRule?.endpoint?.name));
        setErrorMessage('');
        setStatusMessage('');
        setEditModalOpen(true);
    }, [canManageCredentials, setErrorMessage, setStatusMessage]);

    /**
     * Saves edits for the selected outbound rule and destination.
     */
    const handleSaveEditRule = useCallback(async () => {
        if (!baseId || !canManageCredentials) return;

        const ruleId = cleanString(editingRule?.id || editingRule?.key);
        const webhookId = cleanString(editingRule?.endpoint?.id || editingRule?.webhookId);
        const name = cleanString(editDraftName);
        const event = normalizeRuleEvent(editDraftEvent);
        const typeId = cleanString(editDraftTypeId);
        const status = cleanString(editDraftStatus).toLowerCase() === 'paused' ? 'paused' : 'active';
        const destinationUrl = cleanString(editDraftDestinationUrl);
        const destinationName = cleanString(editDraftDestinationName);

        if (!ruleId || !name || !typeId || !destinationUrl) {
            setErrorMessage('Rule name, trigger type, and destination URL are required.');
            return;
        }

        setBusyActionKey(`edit:${ruleId}`);
        setErrorMessage('');
        setStatusMessage('');

        try {
            if (webhookId) {
                await updateBaseWebhookEndpoint({
                    baseId,
                    webhookId,
                    patch: {
                        name: destinationName || `${name} Destination`,
                        url: destinationUrl,
                    },
                });
            }

            await updateBaseWebhookRule({
                baseId,
                ruleId,
                patch: {
                    name,
                    event,
                    typeId,
                    status,
                },
            });

            toast.success('Outbound rule updated.');
            setStatusMessage('Outbound rule updated.');
            setEditModalOpen(false);
            setEditingRule(null);
            await loadRules();
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to update outbound rule.');
        } finally {
            setBusyActionKey('');
        }
    }, [
        canManageCredentials,
        editDraftDestinationName,
        editDraftDestinationUrl,
        editDraftEvent,
        editDraftName,
        editDraftStatus,
        editDraftTypeId,
        editingRule,
        loadRules,
        setBusyActionKey,
        setErrorMessage,
        setStatusMessage,
        baseId,
    ]);

    /**
     * Toggles active/paused status for an outbound rule.
     *
     * @param {object} rule - Outbound rule row to update.
     */
    const handleToggleRuleStatus = useCallback(async (rule) => {
        const ruleId = cleanString(rule?.id || rule?.key);
        if (!baseId || !ruleId || !canManageCredentials) return;

        const currentStatus = cleanString(rule?.status || 'active').toLowerCase();
        const nextStatus = currentStatus === 'active' ? 'paused' : 'active';

        setBusyActionKey(`toggle:${ruleId}`);
        setErrorMessage('');
        setStatusMessage('');

        try {
            await updateBaseWebhookRule({
                baseId,
                ruleId,
                patch: {
                    status: nextStatus,
                },
            });

            toast.success(`Rule ${nextStatus === 'active' ? 'activated' : 'paused'}.`);
            setStatusMessage(`Rule ${nextStatus === 'active' ? 'activated' : 'paused'}.`);
            await loadRules();
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to update outbound rule status.');
        } finally {
            setBusyActionKey('');
        }
    }, [canManageCredentials, loadRules, setBusyActionKey, setErrorMessage, setStatusMessage, baseId]);

    /**
     * Queues a test delivery for an outbound rule.
     *
     * @param {object} rule - Outbound rule row to test.
     */
    const handleTestRule = useCallback(async (rule) => {
        const ruleId = cleanString(rule?.id || rule?.key);
        if (!baseId || !ruleId || !canManageCredentials) return;

        setBusyActionKey(`test:${ruleId}`);
        setErrorMessage('');
        setStatusMessage('');

        try {
            const response = await testBaseWebhookRule({ baseId, ruleId });
            const operationId = cleanString(response?.operationId);
            const message = operationId
                ? `Test delivery queued (operation ${operationId}).`
                : 'Test delivery queued.';
            toast.success('Test delivery queued.');
            setStatusMessage(message);
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to queue test delivery.');
        } finally {
            setBusyActionKey('');
        }
    }, [canManageCredentials, setBusyActionKey, setErrorMessage, setStatusMessage, baseId]);

    /**
     * Opens delete confirmation for an outbound rule.
     *
     * @param {object} rule - Outbound rule row pending delete.
     */
    const handleDeletePrompt = useCallback((rule) => {
        if (!canManageCredentials) return;
        setPendingDeleteRule(rule || null);
        setDeleteConfirmOpen(true);
    }, [canManageCredentials]);

    /**
     * Updates outbound delete confirm open state.
     *
     * @param {boolean} nextOpen - Next open state.
     */
    const handleDeleteConfirmOpen = useCallback((nextOpen) => {
        setDeleteConfirmOpen(Boolean(nextOpen));
        if (!nextOpen) {
            setPendingDeleteRule(null);
        }
    }, []);

    /**
     * Deletes the selected outbound rule.
     */
    const handleDeleteRule = useCallback(async () => {
        const ruleId = cleanString(pendingDeleteRule?.id || pendingDeleteRule?.key);
        if (!baseId || !ruleId || !canManageCredentials) return;

        setDeleteConfirmOpen(false);
        setBusyActionKey(`delete:${ruleId}`);
        setErrorMessage('');
        setStatusMessage('');

        try {
            await deleteBaseWebhookRule({ baseId, ruleId });
            toast.success('Outbound rule deleted.');
            setStatusMessage('Outbound rule deleted.');
            await loadRules();
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to delete outbound rule.');
        } finally {
            setBusyActionKey('');
            setPendingDeleteRule(null);
        }
    }, [canManageCredentials, loadRules, pendingDeleteRule, setBusyActionKey, setErrorMessage, setStatusMessage, baseId]);

    /**
     * Closes the create outbound rule modal.
     */
    const handleCreateModalClose = useCallback(() => {
        setCreateModalOpen(false);
    }, []);

    /**
     * Closes the edit outbound rule modal and clears selected rule.
     */
    const handleEditModalClose = useCallback(() => {
        setEditModalOpen(false);
        setEditingRule(null);
    }, []);


    // ----- RETURN ----- //

    return {
        loading,
        sortedRules,
        createModalOpen,
        draftName,
        draftEvent,
        draftTypeId,
        draftDestinationUrl,
        draftDestinationName,
        editModalOpen,
        editingRule,
        editDraftName,
        editDraftEvent,
        editDraftTypeId,
        editDraftStatus,
        editDraftDestinationUrl,
        editDraftDestinationName,
        deleteConfirmOpen,
        deleteConfirmMessage,
        setDraftName,
        setDraftEvent,
        setDraftTypeId,
        setDraftDestinationUrl,
        setDraftDestinationName,
        setEditDraftName,
        setEditDraftEvent,
        setEditDraftTypeId,
        setEditDraftStatus,
        setEditDraftDestinationUrl,
        setEditDraftDestinationName,
        handleOpenCreateModal,
        handleCreateRule,
        handleOpenEditModal,
        handleSaveEditRule,
        handleToggleRuleStatus,
        handleTestRule,
        handleDeletePrompt,
        handleDeleteConfirmOpen,
        handleDeleteRule,
        handleCreateModalClose,
        handleEditModalClose,
    };
};

export default useOutboundIntegrations;
